/** Public website landing pages only. No inference, AI calls or SKU merging. */
type GroupProduct = { product_ru?: string; product_ro?: string; product_uk?: string; category_id?: string; offer_notice?: { identity_verified?: boolean } };
type GroupLocale = "ru" | "ro" | "uk";
const words = (value: string) => value.toLocaleLowerCase("ru").normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "").replace(/ё/g, "е").replace(/[^\p{L}\p{N}]+/gu, " ").trim();
// Match names, not notes/category labels: "without sugar" is not a sugar offer.
const names = (product: GroupProduct) => [product.product_ru, product.product_ro, product.product_uk]
  .filter((value): value is string => typeof value === "string").map(words);
const group = (id: string, slugs: Record<GroupLocale, string>, labels: Record<GroupLocale, string>,
  include: RegExp, exclude: RegExp, categories: readonly string[]) => ({ id, slugs, labels, include, exclude, categories });

export const WEBSITE_PRODUCT_GROUPS = [
  group("butter", { ru: "slivochnoe-maslo", ro: "unt", uk: "vershkove-maslo" },
    { ru: "Сливочное масло", ro: "Unt", uk: "Вершкове масло" },
    /(?:сливочн\S*\s+масл|масл\S*\s+сливочн|вершков\S*\s+масл|масл\S*\s+вершков|\bunt\b)/u,
    /(?:спред|маргарин|растител|рослин|margar|spread|spred|vegetal|arahid|какао|шоколад|арахис|untura)/u, ["dairy_eggs"]),
  group("sunflower-oil", { ru: "podsolnechnoe-maslo", ro: "ulei-de-floarea-soarelui", uk: "soniashnykova-oliia" },
    { ru: "Подсолнечное масло", ro: "Ulei de floarea-soarelui", uk: "Соняшникова олія" },
    /(?:подсолнеч\S*.{0,24}масл|масл\S*.{0,24}подсолнеч|соняшник\S*.{0,24}олі|олі\S*.{0,24}соняшник|\bulei\b.{0,30}floarea\s+soarelui)/u,
    /(?:смесь|суміш|amestec|mix|майонез|maionez)/u, ["pantry"]),
  group("sugar", { ru: "sahar", ro: "zahar", uk: "tsukor" },
    { ru: "Сахар", ro: "Zahăr", uk: "Цукор" }, /(?:\bzahar\b|(?:^| )сахар(?: |$)|(?:^| )цукор(?: |$))/u,
    /(?:без|fara|cu\s+zahar|pudra|пудр|vanil|ванил|gelifiant|желир|сироп|sirop|biscuit|печень|конфет|ciocolat)/u, ["pantry"]),
  group("wheat-flour", { ru: "pshenichnaya-muka", ro: "faina-de-grau", uk: "pshenychne-boroshno" },
    { ru: "Пшеничная мука", ro: "Făină de grâu", uk: "Пшеничне борошно" },
    /(?:мук\S*.{0,24}пшенич|пшенич\S*.{0,24}мук|борошн\S*.{0,24}пшенич|пшенич\S*.{0,24}борошн|\bfaina\b.{0,24}\bgrau\b)/u,
    /(?:amestec|смесь|суміш|porumb|кукуруз|кукурудз|рисов|orez|secar|ржан|житн)/u, ["pantry"]),
  group("eggs", { ru: "kurinye-yaytsa", ro: "oua-de-gaina", uk: "kuriachi-yaitsia" },
    { ru: "Куриные яйца", ro: "Ouă de găină", uk: "Курячі яйця" }, /(?:яиц|яєц|\boua\b)/u,
    /(?:перепел|перепіл|prepel|страус|strut|ciocolat|шоколад|melanj|меланж)/u, ["dairy_eggs"]),
  group("milk", { ru: "moloko", ro: "lapte", uk: "moloko" },
    { ru: "Питьевое молоко", ro: "Lapte de consum", uk: "Питне молоко" }, /(?:(?:^| )молоко(?: |$)|\blapte\b)/u,
    /(?:сыр|сир|branza|cascaval|covasit|ряжен|produs|preparat|продукт|сгущ|згущ|сух\S*\s+молок|condens|\bpraf\b|pulbere|uscat|batut|кисломол|кисле|растител|рослин|vegetal|soia|ovaz|migdale|cocos|соев|овсян|вівсян|кокос|напиток|напіи|bautura|ciocolat|какао|коктеил)/u, ["dairy_eggs"]),
  group("chicken-fillet", { ru: "kurinoe-file", ro: "file-de-pui", uk: "kuriache-file" },
    { ru: "Куриное филе", ro: "File de pui", uk: "Куряче філе" },
    /(?:курин|куриц|куряч|\bpui\b|\bgaina\b)/u,
    /(?:колбас|ковбас|salam|carnati|рулет|rulad|rulou|копчен|копчено|afumat|панир|pesmet|marinat|маринов|шницел|snitel|nugget|наггет|tocat|фарш|parizer)/u, ["meat_deli"]),
  group("coffee-beans", { ru: "zernovoy-kofe", ro: "cafea-boabe", uk: "zernova-kava" },
    { ru: "Зерновой кофе", ro: "Cafea boabe", uk: "Зернова кава" },
    /(?:кофе.{0,24}(?:зерн|зерен)|(?:зерн|зерен).{0,24}кофе|кав\S*.{0,24}зерн|зерн\S*.{0,24}кав|\bcafea\b.{0,24}\bboabe\b)/u,
    /(?:растворим|розчинн|молот|мелен|macinat|solubil|instant|capsul|капсул|cicoare|цикор|цикорі|какао|cacao|3\s*(?:in|в)\s*1)/u, ["pantry", "soft_drinks"]),
] as const;

export function websiteProductGroup(id: string | undefined) {
  return WEBSITE_PRODUCT_GROUPS.find(group => group.id === id);
}

export function matchesWebsiteProductGroup(product: GroupProduct, id: string): boolean {
  const group = websiteProductGroup(id);
  if (!group || product.offer_notice?.identity_verified === false || !group.categories.includes(product.category_id ?? "")) return false;
  const titles = names(product);
  if (titles.some(title => group.exclude.test(title))) return false;
  if (id === "chicken-fillet" && !titles.some(title => /(?:филе|філе|\bfile\b)/u.test(title)
      || (/(?:груд|\bpiept\b)/u.test(title) && /(?:без\s+кост|без\s+кіст|fara\s+os|dezosat)/u.test(title)))) return false;
  return titles.some(title => group.include.test(title));
}

export function websiteGroupPath(id: string, locale: GroupLocale): string {
  const group = websiteProductGroup(id);
  if (!group) throw new Error("Unknown website product group");
  return `/${locale}/${locale === "ro" ? "produse" : "produkty"}/${group.slugs[locale]}/`;
}
